import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  of,
  switchMap,
} from 'rxjs';

import { Cliente } from '@core/models/models';
import { ClienteService } from '@core/services/domain-services';
import { FeedbackService } from '@core/services/feedback.service';
import { extractErrorMessage } from '@core/utils/error-parser.util';
import {
  EnumDocumentStatus,
  EnumPaymentStatus,
  FeDocumento,
  FeTipoDocumento,
  FormatoDocumentoPdf,
} from '@shared/api/models/faturacao-eletronica.model';
import { FaturacaoEletronicaService } from '@shared/api/services/faturacao-eletronica.service';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { FaturaDrawerComponent } from './components/fatura-drawer/fatura-drawer.component';

export type StatusTabKey =
  | 'todas'
  | 'rascunhos'
  | 'submetidas'
  | 'aceites'
  | 'pagas'
  | 'rejeitadas';

@Component({
  selector: 'app-financeiro',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DatePickerModule,
    InputTextModule,
    MenuModule,
    PaginatorModule,
    PaginatorControlComponent,
    SelectModule,
    SkeletonModule,
    TooltipModule,
    EmptyStateComponent,
    FaturaDrawerComponent,
  ],
  templateUrl: './financeiro.component.html',
  styleUrl: './financeiro.component.scss',
})
export class FinanceiroComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly faturacao = inject(FaturacaoEletronicaService);
  private readonly clienteService = inject(ClienteService);
  private readonly feedback = inject(FeedbackService);
  private readonly destroyRef = inject(DestroyRef);

  // Enum references for template
  readonly EnumDocumentStatus = EnumDocumentStatus;

  // Data collections
  documentos: FeDocumento[] = [];
  clientes: Cliente[] = [];
  tiposDocumento: FeTipoDocumento[] = [];

  // State & Loading
  loading = false;
  loadingAuxiliares = false;
  actionId: number | null = null;
  downloadingPdfId: number | null = null;

  // Pagination & Filtering
  totalRecords = 0;
  currentPage = 1;
  pageSize = 10;
  activeTab: StatusTabKey = 'todas';

  filtrosForm!: FormGroup;

  // Drawer state
  showDrawer = false;
  drawerModo: 'criar' | 'retificar' = 'criar';
  drawerDocumento: FeDocumento | null = null;

  // Tab counts
  countTodas = 0;
  countRascunhos = 0;
  countSubmetidas = 0;
  countAceites = 0;
  countPagas = 0;
  countRejeitadas = 0;

  // Total amount
  totalFaturadoGeral = 0;

  // Action Menu (Padrão Efetivos)
  activeMenuDoc: FeDocumento | null = null;
  menuItems: MenuItem[] = [];
  private readonly docDetailsCache = new Map<number, FeDocumento>();
  private readonly clientesCache = new Map<number, Cliente>();

  ngOnInit(): void {
    this.initFiltrosForm();
    this.carregarAuxiliares();
    this.carregarDocumentos();
  }

  private initFiltrosForm(): void {
    this.filtrosForm = this.fb.group({
      termoPesquisa: [''],
      idCliente: [null],
      dataInicial: [null],
      dataFinal: [null],
    });

    // Debounced search on typing
    this.filtrosForm
      .get('termoPesquisa')
      ?.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.carregarDocumentos();
      });

    // Immediate filter on client or date change
    this.filtrosForm
      .get('idCliente')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentPage = 1;
        this.carregarDocumentos();
      });
  }

  carregarAuxiliares(): void {
    this.loadingAuxiliares = true;
    forkJoin({
      clientes: this.clienteService
        .getAll({ pageSize: 100 })
        .pipe(catchError(() => of({ data: [] }))),
      tipos: this.faturacao
        .listarTiposDocumento()
        .pipe(catchError(() => of([]))),
    })
      .pipe(
        finalize(() => (this.loadingAuxiliares = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.clientes = res.clientes.data || [];
          this.tiposDocumento = res.tipos || [];
          this.clientes.forEach((c) => {
            if (c.id) {
              this.clientesCache.set(c.id, c);
            }
          });
          if (this.documentos.length) {
            this.enriquecerDocumentosComCliente(this.documentos);
          }
        },
      });
  }

  carregarDocumentos(): void {
    this.loading = true;
    const raw = this.filtrosForm.getRawValue();

    // Map active tab to backend estado parameter
    let estadoParam: string | undefined;
    switch (this.activeTab) {
      case 'rascunhos':
        estadoParam = EnumDocumentStatus.Guardada;
        break;
      case 'submetidas':
        estadoParam = EnumDocumentStatus.Submetida;
        break;
      case 'aceites':
        estadoParam = EnumDocumentStatus.Aceite;
        break;
      case 'rejeitadas':
        estadoParam = EnumDocumentStatus.Rejeitada;
        break;
      case 'pagas':
        // Paid invoices are in Aceite state
        estadoParam = EnumDocumentStatus.Aceite;
        break;
      default:
        estadoParam = undefined;
    }

    const termo = (raw.termoPesquisa || '').trim();

    this.faturacao
      .listarDocumentos({
        documentNo: termo || undefined,
        estado: estadoParam,
        idCliente: raw.idCliente ? Number(raw.idCliente) : undefined,
        dataInicial: raw.dataInicial
          ? this.formatDate(raw.dataInicial)
          : undefined,
        dataFinal: raw.dataFinal ? this.formatDate(raw.dataFinal) : undefined,
        page: this.currentPage,
        pageSize: this.pageSize,
      })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (paged) => {
          this.documentos = paged.items || [];
          this.totalRecords = paged.totalRecords;
          this.atualizarEstatisticas(this.documentos, paged.totalRecords);
          this.enriquecerDocumentosComCliente(this.documentos);
        },
        error: (err: unknown) => {
          this.feedback.showError(
            `Erro ao carregar lista de faturas: ${extractErrorMessage(err)}`,
          );
        },
      });
  }

  private enriquecerDocumentosComCliente(docs: FeDocumento[]): void {
    docs.forEach((doc) => {
      // 1. Se já tem idCliente, busca dados do cliente directamente
      if (doc.idCliente) {
        this.resolverClienteDoDocumento(doc, doc.idCliente);
        return;
      }

      // 2. Se está no cache de detalhes do documento
      if (this.docDetailsCache.has(doc.id)) {
        const cached = this.docDetailsCache.get(doc.id)!;
        doc.idCliente = cached.idCliente;
        doc.documentDate = cached.documentDate;
        if (cached.idCliente) {
          this.resolverClienteDoDocumento(doc, cached.idCliente);
        }
        return;
      }

      // 3. Busca o detalhe do documento para obter o idCliente
      this.faturacao
        .obterDocumento(doc.id)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => of(null)),
        )
        .subscribe((detalhe) => {
          if (detalhe) {
            this.docDetailsCache.set(doc.id, detalhe);
            doc.idCliente = detalhe.idCliente;
            doc.documentDate = detalhe.documentDate;
            if (detalhe.idCliente) {
              this.resolverClienteDoDocumento(doc, detalhe.idCliente);
            }
          }
        });
    });
  }

  private resolverClienteDoDocumento(
    doc: FeDocumento,
    idCliente: number,
  ): void {
    // 1. Cache local por ID
    if (this.clientesCache.has(idCliente)) {
      const cli = this.clientesCache.get(idCliente)!;
      this.atribuirCliente(doc, cli);
      return;
    }

    // 2. Lista carregada
    const match = this.clientes.find((c) => c.id === idCliente);
    if (match) {
      this.clientesCache.set(idCliente, match);
      this.atribuirCliente(doc, match);
      return;
    }

    // 3. Busca directamente pelo endpoint /api/Clientes/{id} usando o ID
    this.clienteService
      .getById(idCliente)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((cli) => {
        if (cli) {
          this.clientesCache.set(idCliente, cli);
          this.atribuirCliente(doc, cli);
        }
      });
  }

  private atribuirCliente(doc: FeDocumento, cli: Cliente): void {
    doc.cliente = {
      id: cli.id,
      nome: cli.nome,
      nif: cli.nif,
      email: cli.email,
      endereco: cli.endereco,
      numTelefone: cli.numTelefone,
      nomeResponsavel: cli.nomeResponsavel,
      sector: cli.sector,
    };
  }

  setTab(tab: StatusTabKey): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.currentPage = 1;
    this.carregarDocumentos();
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = (event.page ?? 0) + 1;
    this.pageSize = event.rows ?? this.pageSize;
    this.carregarDocumentos();
  }

  limparFiltros(): void {
    this.filtrosForm.reset();
    this.activeTab = 'todas';
    this.currentPage = 1;
    this.carregarDocumentos();
  }

  // Estatísticas e contadores
  private atualizarEstatisticas(docs: FeDocumento[], total: number): void {
    if (this.activeTab === 'todas') {
      this.countTodas = total;
      this.faturacao.totalDocumentos.set(total);
    }

    // Calculate sum of grossTotal
    const soma = docs.reduce((acc, d) => acc + (Number(d.grossTotal) || 0), 0);
    if (soma > 0) {
      this.totalFaturadoGeral = soma;
    }

    // Counts by status in loaded items
    let rascunhos = 0;
    let submetidas = 0;
    let aceites = 0;
    let pagas = 0;
    let rejeitadas = 0;

    docs.forEach((doc) => {
      const status = this.statusOf(doc);
      if (status === EnumDocumentStatus.Guardada) rascunhos++;
      if (status === EnumDocumentStatus.Submetida) submetidas++;
      if (status === EnumDocumentStatus.Aceite) aceites++;
      if (status === EnumDocumentStatus.Rejeitada) rejeitadas++;
      if (this.isPago(doc)) pagas++;
    });

    if (this.activeTab === 'todas') {
      this.countRascunhos = rascunhos;
      this.countSubmetidas = submetidas;
      this.countAceites = aceites;
      this.countPagas = pagas;
      this.countRejeitadas = rejeitadas;
    }
  }

  // Status mapping
  statusOf(doc: FeDocumento): EnumDocumentStatus {
    const raw = doc.estado ?? doc.documentStatus ?? '';
    if (typeof raw === 'number') {
      return (
        {
          1: EnumDocumentStatus.Guardada,
          2: EnumDocumentStatus.Submetida,
          3: EnumDocumentStatus.Aceite,
          4: EnumDocumentStatus.Rejeitada,
        }[raw] ?? EnumDocumentStatus.Guardada
      );
    }
    const str = String(raw).toLowerCase();
    if (str.includes('aceit')) return EnumDocumentStatus.Aceite;
    if (str.includes('submet') || str.includes('processam'))
      return EnumDocumentStatus.Submetida;
    if (str.includes('rejeit')) return EnumDocumentStatus.Rejeitada;
    return EnumDocumentStatus.Guardada;
  }

  isPago(doc: FeDocumento): boolean {
    const raw = String(doc.paymentStatus ?? '');
    return (
      raw === '3' ||
      raw === 'PagoTotalmente' ||
      raw === 'Pago' ||
      raw === 'Liquidado'
    );
  }

  statusLabel(doc: FeDocumento): string {
    const status = this.statusOf(doc);
    switch (status) {
      case EnumDocumentStatus.Aceite:
        return 'Aceite';
      case EnumDocumentStatus.Submetida:
        return 'Submetido';
      case EnumDocumentStatus.Rejeitada:
        return 'Rejeitado';
      case EnumDocumentStatus.Guardada:
      default:
        return 'Pendente';
    }
  }

  statusBadgeClass(doc: FeDocumento): string {
    const status = this.statusOf(doc);
    switch (status) {
      case EnumDocumentStatus.Aceite:
        return 'badge-aceite';
      case EnumDocumentStatus.Submetida:
        return 'badge-submetida';
      case EnumDocumentStatus.Rejeitada:
        return 'badge-rejeitada';
      case EnumDocumentStatus.Guardada:
      default:
        return 'badge-guardada';
    }
  }

  reciboLabel(doc: FeDocumento): string {
    return this.isPago(doc) ? 'Emitido' : 'Pendente';
  }

  reciboBadgeClass(doc: FeDocumento): string {
    return this.isPago(doc) ? 'badge-recibo-emitido' : 'badge-recibo-pendente';
  }

  clientName(doc: FeDocumento): string {
    if (doc.cliente?.nome) return doc.cliente.nome;
    if (doc.idCliente) {
      if (this.clientesCache.has(doc.idCliente)) {
        return this.clientesCache.get(doc.idCliente)!.nome;
      }
      const match = this.clientes.find((c) => c.id === doc.idCliente);
      if (match?.nome) return match.nome;
      return `Cliente #${doc.idCliente}`;
    }
    return 'A carregar...';
  }

  clientNif(doc: FeDocumento): string {
    if (doc.cliente?.nif) return doc.cliente.nif;
    if (doc.idCliente) {
      if (this.clientesCache.has(doc.idCliente)) {
        return this.clientesCache.get(doc.idCliente)!.nif || '';
      }
      const match = this.clientes.find((c) => c.id === doc.idCliente);
      if (match?.nif) return match.nif || '';
    }
    return '';
  }

  clientPhone(doc: FeDocumento): string {
    if (doc.cliente?.numTelefone) return doc.cliente.numTelefone;
    if (doc.idCliente && this.clientesCache.has(doc.idCliente)) {
      return this.clientesCache.get(doc.idCliente)!.numTelefone || '';
    }
    return '';
  }

  // ── Menu de Opções por Linha (Padrão Efetivos) ────────────
  openActions(event: Event, doc: FeDocumento, menu: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.activeMenuDoc = doc;
    this.buildMenuItems(doc);
    menu.toggle(event);
  }

  private buildMenuItems(doc: FeDocumento): void {
    const status = this.statusOf(doc);
    const items: MenuItem[] = [
      {
        label: 'Ver Detalhes',
        icon: 'pi pi-eye',
        command: () => this.abrirDetalhe(doc),
      },
      {
        label: 'Descarregar PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.descarregarPdf(doc),
      },
    ];

    if (status === EnumDocumentStatus.Guardada) {
      items.push(
        { separator: true },
        {
          label: 'Submeter à AGT',
          icon: 'pi pi-send',
          command: () => this.submeterAgt(doc),
        },
        {
          label: 'Editar Fatura',
          icon: 'pi pi-pencil',
          command: () => this.abrirCorrigir(doc),
        },
      );
    } else if (status === EnumDocumentStatus.Submetida) {
      items.push(
        { separator: true },
        {
          label: 'Consultar Estado AGT',
          icon: 'pi pi-refresh',
          command: () => this.consultarEstadoAgt(doc),
        },
      );
    } else if (status === EnumDocumentStatus.Aceite) {
      items.push(
        { separator: true },
        {
          label: 'Consultar Estado AGT',
          icon: 'pi pi-refresh',
          command: () => this.consultarEstadoAgt(doc),
        },
      );
      if (!this.isPago(doc)) {
        items.push({
          label: 'Registar Pagamento',
          icon: 'pi pi-wallet',
          command: () => this.abrirDetalhe(doc),
        });
      }
    } else if (status === EnumDocumentStatus.Rejeitada) {
      items.push(
        { separator: true },
        {
          label: 'Corrigir Fatura',
          icon: 'pi pi-wrench',
          styleClass: 'menu-item-danger',
          command: () => this.abrirCorrigir(doc),
        },
        {
          label: 'Consultar Estado AGT',
          icon: 'pi pi-refresh',
          command: () => this.consultarEstadoAgt(doc),
        },
      );
    }

    this.menuItems = items;
  }

  // Navegação e Ações
  abrirDetalhe(doc: FeDocumento): void {
    this.router.navigate(['/admin/financeiro', doc.id]);
  }

  abrirWizardNovo(): void {
    this.router.navigate(['/admin/financeiro/nova']);
  }

  abrirDrawerNovo(): void {
    this.drawerModo = 'criar';
    this.drawerDocumento = null;
    this.showDrawer = true;
  }

  abrirCorrigir(doc: FeDocumento): void {
    this.drawerModo = 'retificar';
    this.drawerDocumento = doc;
    this.showDrawer = true;
  }

  onDrawerSaved(savedDoc: FeDocumento): void {
    this.carregarDocumentos();
  }

  submeterAgt(doc: FeDocumento, event?: Event): void {
    if (event) event.stopPropagation();
    this.actionId = doc.id;
    this.faturacao
      .submeterDocumento(doc.id)
      .pipe(
        finalize(() => (this.actionId = null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.feedback.showSuccess('Documento submetido à AGT com sucesso.');
          this.carregarDocumentos();
        },
        error: (err: unknown) => {
          this.feedback.showError(
            `Erro na submissão à AGT: ${extractErrorMessage(err)}`,
          );
        },
      });
  }

  consultarEstadoAgt(doc: FeDocumento, event?: Event): void {
    if (event) event.stopPropagation();
    this.actionId = doc.id;
    this.faturacao
      .consultarEstado(doc.id)
      .pipe(
        finalize(() => (this.actionId = null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (atualizado) => {
          const status = this.statusOf(atualizado);
          this.feedback.showSuccess(`Estado atualizado: ${status}`);
          this.carregarDocumentos();
        },
        error: (err: unknown) => {
          this.feedback.showError(
            `Erro ao consultar estado: ${extractErrorMessage(err)}`,
          );
        },
      });
  }

  descarregarPdf(doc: FeDocumento, event?: Event): void {
    if (event) event.stopPropagation();
    this.downloadingPdfId = doc.id;
    this.faturacao
      .descarregarPdf(doc.id, FormatoDocumentoPdf.A4)
      .pipe(
        finalize(() => (this.downloadingPdfId = null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${doc.documentNo || 'fatura_' + doc.id}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
          this.feedback.showSuccess('PDF descarregado com sucesso.');
        },
        error: (err: unknown) => {
          this.feedback.showError(
            `Erro ao descarregar PDF: ${extractErrorMessage(err)}`,
          );
        },
      });
  }

  exportarRelatorio(): void {
    if (!this.documentos.length) {
      this.feedback.showWarning('Não existem faturas para exportar.');
      return;
    }

    const headers = [
      'Nº Factura',
      'Cliente',
      'NIF',
      'Data',
      'Estado AGT',
      'Recibo',
      'Total (Kz)',
    ];
    const rows = this.documentos.map((d) => [
      `"${d.documentNo || '#' + d.id}"`,
      `"${this.clientName(d).replace(/"/g, '""')}"`,
      `"${this.clientNif(d)}"`,
      `"${d.documentDate ? new Date(d.documentDate).toLocaleDateString('pt-AO') : ''}"`,
      `"${this.statusLabel(d)}"`,
      `"${this.reciboLabel(d)}"`,
      `"${Number(d.grossTotal || 0).toFixed(2)}"`,
    ]);

    const csvContent =
      '\uFEFF' +
      [headers.join(';'), ...rows.map((e) => e.join(';'))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `relatorio_faturacao_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    link.click();
    URL.revokeObjectURL(url);
    this.feedback.showSuccess('Relatório CSV exportado com sucesso.');
  }

  // Helper date formatting
  private formatDate(d: unknown): string {
    if (!d) return '';
    const date = new Date(d as string | number | Date);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }

  trackByDocId(_index: number, doc: FeDocumento): number {
    return doc.id;
  }

  minVal(a: number, b: number): number {
    return Math.min(a, b);
  }
}
